import { TextUIPart } from 'ai';
import { isEmpty, isNil, pluck } from 'ramda';

import { FileAttachmentMetadata } from '../../../chat.types';
import { LegacyMessageHandler, MessageVariant, SubmitArgs } from '../../ChatUserMessage.types';
import { defaultMessageHandler } from '../ChatUserDefaultMessage/ChatUserDefaultMessage.handler';

export interface File {
  fileId: string;
  fileName: string;
  contentType: string;
  size: number;
}

/**
 * Extract files from message parts.
 * Reconstructs full File objects from the parallel arrays serialized in buildMessageParts.
 */
const extractFiles = (parts?: any[]): File[] => {
  if (!parts || parts.length === 0) {
    return [];
  }

  const firstPart = parts[0];
  if (firstPart.type !== 'text') {
    return [];
  }

  try {
    const parsed = JSON.parse(firstPart.text);
    if (!parsed.fileIds || !Array.isArray(parsed.fileIds)) {
      return [];
    }

    return parsed.fileIds.map((fileId: string, i: number) => ({
      fileId,
      fileName: parsed.fileNames?.[i] ?? '',
      contentType: parsed.contentTypes?.[i] ?? '',
      size: parsed.sizes?.[i] ?? 0,
    }));
  } catch (error) {
    return [];
  }
};

/**
 * Build message parts from submit args
 */
const buildMessageParts = (args: SubmitArgs, files: File[]): TextUIPart[] => {
  const parts: TextUIPart[] = [];

  // Add user text as first part
  // we set a custom type to identify this as a file attachment message which will be handled separately
  parts.push({
    type: 'text' as const,
    text: JSON.stringify({
      annotations: [{ type: MessageVariant.FILE_ATTACHMENT }],
      content: args.text,
      fileIds: pluck('fileId', files),
      fileNames: pluck('fileName', files),
      contentTypes: pluck('contentType', files),
      sizes: pluck('size', files),
    }),
  });

  return parts;
};

export const fileAttachmentMessageHandler: LegacyMessageHandler<FileAttachmentMetadata> = {
  submit: (args, metadata) => {
    if (isNil(metadata) || isEmpty(metadata.files)) {
      return defaultMessageHandler.submit(args);
    }

    const parts = buildMessageParts(args, metadata.files);
    return {
      role: 'user' as const,
      content: args.text,
      parts,
      annotations: [{ type: MessageVariant.FILE_ATTACHMENT }],
      metadata,
    };
  },

  parseParts: (parts) => {
    let content;
    try {
      const parsedText = parts[0].type === 'text' ? JSON.parse(parts[0]?.text) : {};
      content = parsedText.content;
    } catch {
      content = undefined;
    }

    return {
      files: extractFiles(parts),
      content,
    };
  },
};
